import { Fragment, useEffect, useState } from "react";
import { FaCheck, FaChevronDown, FaChevronRight, FaEdit, FaTrash } from "react-icons/fa";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { MdInput, MdOutlineOutput } from "react-icons/md";
import { fetchWithAuth } from "../api/fetchWithAuth";
import EraRequirementFormModal from "../components/EraRequirementFormModal";
import EraRequirementModal from "../components/EraRequirementModal";

export default function EraRequirements({ apiBase = "/api/era-requirements", title = "ERA Requirements", user }) {
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showCoveredOnly, setShowCoveredOnly] = useState(false);
  const [showDmiIn, setShowDmiIn] = useState(false);
  const [showDmiOut, setShowDmiOut] = useState(false);
  const [hideHeadings, setHideHeadings] = useState(false);
  const [showGrouped, setShowGrouped] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortField, setSortField] = useState("req_number");
  const [sortDir, setSortDir] = useState("asc");
  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || roleCodes.includes("RQM");

  useEffect(() => {
    loadRequirements();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const loadRequirements = async () => {
    try {
      const res = await fetchWithAuth(apiBase);

      if (!res.ok) {
        console.error("Failed to load ERA requirements");
        return;
      }

      const data = await res.json();
      setRequirements(data);
    } catch (err) {
      console.warn("loadRequirements aborted:", err.message);
    }
  };

  const deleteRequirement = async (id) => {
    if (!window.confirm("Delete this ERA requirement?")) return;

    const res = await fetchWithAuth(`${apiBase}/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      alert("Delete failed");
      return;
    }

    loadRequirements();
  };

  const openRequirement = (requirement) => {
    if (requirement.node_type === "section") {
      return;
    }

    setSelectedRequirement(requirement);
    setShowModal(true);
  };

  const isSection = (requirement) => requirement.node_type === "section";
  const isRequirement = (requirement) => !isSection(requirement);

  const filteredRequirements = requirements.filter((requirement) => {
    const query = debouncedSearch.trim().toLowerCase();
    const matchesSearch = !query || (
      String(requirement.req_number ?? "").toLowerCase().includes(query) ||
      String(requirement.req_title ?? "").toLowerCase().includes(query)
    );

    if (!matchesSearch) return false;

    if (hideHeadings && isSection(requirement)) {
      return false;
    }

    if (showCoveredOnly && Number(requirement.covering_test_cases_count ?? 0) === 0) {
      return false;
    }

    const isIn = Boolean(requirement.dmi_in_all);
    const isOut = Boolean(requirement.dmi_out_all);

    if (showDmiIn && showDmiOut) {
      return isIn && isOut;
    }

    if (showDmiIn) {
      return isIn;
    }

    if (showDmiOut) {
      return isOut;
    }

    return true;
  });

  const buildRequirementNumberSortKey = (value) => {
    const text = String(value ?? "").trim();
    const match = text.match(/^(A\.)?(\d+(?:\.\d+)*)(?:-(.*))?$/i);

    if (!match) {
      return {
        section: Number.MAX_SAFE_INTEGER,
        appendix: 0,
        mainParts: [],
        suffixParts: [],
        text
      };
    }

    const mainParts = match[2].split(".");

    return {
      section: Number(mainParts[0]),
      appendix: match[1] ? 1 : 0,
      mainParts,
      suffixParts: match[3] ? match[3].split(/[.-]/).filter(Boolean) : [],
      text
    };
  };

  const compareNumberPart = (leftPart, rightPart) => {
    const leftNumber = Number(leftPart);
    const rightNumber = Number(rightPart);
    const bothNumeric = !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber);

    if (bothNumeric) {
      if (leftNumber < rightNumber) return -1;
      if (leftNumber > rightNumber) return 1;
      return 0;
    }

    const leftNumeric = !Number.isNaN(leftNumber);
    const rightNumeric = !Number.isNaN(rightNumber);

    if (leftNumeric !== rightNumeric) {
      return leftNumeric ? -1 : 1;
    }

    return String(leftPart).localeCompare(String(rightPart), undefined, {
      sensitivity: "base",
      numeric: true
    });
  };

  const compareRequirementNumbers = (leftValue, rightValue) => {
    const leftKey = buildRequirementNumberSortKey(leftValue);
    const rightKey = buildRequirementNumberSortKey(rightValue);

    if (leftKey.section !== rightKey.section) {
      return leftKey.section - rightKey.section;
    }

    if (leftKey.appendix !== rightKey.appendix) {
      return leftKey.appendix - rightKey.appendix;
    }

    const maxMainLength = Math.max(leftKey.mainParts.length, rightKey.mainParts.length);

    for (let index = 0; index < maxMainLength; index += 1) {
      const leftPart = leftKey.mainParts[index];
      const rightPart = rightKey.mainParts[index];

      if (leftPart === undefined) return -1;
      if (rightPart === undefined) return 1;

      const partCompare = compareNumberPart(leftPart, rightPart);
      if (partCompare !== 0) return partCompare;
    }

    if (!leftKey.suffixParts.length && rightKey.suffixParts.length) return -1;
    if (leftKey.suffixParts.length && !rightKey.suffixParts.length) return 1;

    const maxSuffixLength = Math.max(leftKey.suffixParts.length, rightKey.suffixParts.length);

    for (let index = 0; index < maxSuffixLength; index += 1) {
      const leftPart = leftKey.suffixParts[index];
      const rightPart = rightKey.suffixParts[index];

      if (leftPart === undefined) return -1;
      if (rightPart === undefined) return 1;

      const partCompare = compareNumberPart(leftPart, rightPart);
      if (partCompare !== 0) return partCompare;
    }

    return leftKey.text.localeCompare(rightKey.text, undefined, { sensitivity: "base", numeric: true });
  };

  const compareRequirements = (leftRequirement, rightRequirement) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const leftIsSection = isSection(leftRequirement);
    const rightIsSection = isSection(rightRequirement);

    if (sortField === "req_number") {
      const numberCompare = compareRequirementNumbers(
        leftRequirement.req_number,
        rightRequirement.req_number
      );

      if (numberCompare !== 0) return numberCompare * direction;

      if (leftIsSection !== rightIsSection) {
        return leftIsSection ? -1 : 1;
      }
    }

    if (sortField === "req_title") {
      const titleCompare = String(leftRequirement.req_title ?? "").localeCompare(
        String(rightRequirement.req_title ?? ""),
        undefined,
        { sensitivity: "base" }
      );

      if (titleCompare !== 0) return titleCompare * direction;
    }

    if (sortField === "dmi_in_all") {
      const inCompare = Number(Boolean(leftRequirement.dmi_in_all)) - Number(Boolean(rightRequirement.dmi_in_all));
      if (inCompare !== 0) return inCompare * direction;
    }

    if (sortField === "dmi_out_all") {
      const outCompare = Number(Boolean(leftRequirement.dmi_out_all)) - Number(Boolean(rightRequirement.dmi_out_all));
      if (outCompare !== 0) return outCompare * direction;
    }

    const numberCompare = compareRequirementNumbers(
      leftRequirement.req_number,
      rightRequirement.req_number
    );

    if (numberCompare !== 0) return numberCompare;

    if (leftIsSection !== rightIsSection) {
      return leftIsSection ? -1 : 1;
    }

    return String(leftRequirement.req_title ?? "").localeCompare(
      String(rightRequirement.req_title ?? ""),
      undefined,
      { sensitivity: "base" }
    );
  };

  const sortedRequirements = [...filteredRequirements].sort(compareRequirements);

  const groupedRequirements = Object.values(
    sortedRequirements.reduce((groups, requirement) => {
      const groupKey = requirement.req_number ?? "";
      const existingGroup = groups[groupKey];

      if (existingGroup) {
        existingGroup.children.push(requirement);
        existingGroup.allDmiIn = existingGroup.allDmiIn && Boolean(requirement.dmi_in_all);
        existingGroup.allDmiOut = existingGroup.allDmiOut && Boolean(requirement.dmi_out_all);
        return groups;
      }

      groups[groupKey] = {
        req_number: requirement.req_number,
        children: [requirement],
        node_type: requirement.node_type,
        allDmiIn: Boolean(requirement.dmi_in_all),
        allDmiOut: Boolean(requirement.dmi_out_all)
      };

      return groups;
    }, {})
  ).sort((leftGroup, rightGroup) => {
    const leftRepresentative = {
      req_number: leftGroup.req_number,
      req_title: leftGroup.children[0]?.req_title ?? "",
      dmi_in_all: leftGroup.allDmiIn,
      dmi_out_all: leftGroup.allDmiOut
    };
    const rightRepresentative = {
      req_number: rightGroup.req_number,
      req_title: rightGroup.children[0]?.req_title ?? "",
      dmi_in_all: rightGroup.allDmiIn,
      dmi_out_all: rightGroup.allDmiOut
    };

    return compareRequirements(leftRepresentative, rightRepresentative);
  });
  const totalVisibleItems = showGrouped
    ? groupedRequirements.reduce((count, group) => (
      count + group.children.filter((requirement) => isRequirement(requirement)).length
    ), 0)
    : sortedRequirements.filter((requirement) => isRequirement(requirement)).length;

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const toggleGroup = (reqNumber) => {
    setExpandedGroups((current) => ({
      ...current,
      [reqNumber]: !current[reqNumber]
    }));
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((current) => current === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDir("asc");
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  const getSectionRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(13, 110, 253, 0.07)" : "rgba(13, 110, 253, 0.03)"
  });

  const renderCoverageIcon = () => (
    <FaCheck size={14} color="#198754" />
  );

  const renderIconFilterButton = (active, onClick, label, content) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
      style={{
        width: "2rem",
        height: "2rem",
        padding: 0,
        borderColor: active ? "#0d6efd" : undefined,
        boxShadow: active ? "inset 0 0 0 1px rgba(13, 110, 253, 0.35)" : undefined,
        backgroundColor: active ? "rgba(13, 110, 253, 0.22)" : undefined
      }}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      {content}
    </button>
  );

  const renderDmiFlags = (item) => {
    if (isSection(item)) return null;

    return (
      <div className="d-inline-grid" style={{ gridTemplateColumns: "repeat(2, 1.35rem)", gap: "0.15rem" }}>
        <span
          className={`d-inline-flex align-items-center justify-content-center ${item.dmi_in_all ? "" : "text-muted opacity-25"}`}
          style={{ width: "1.35rem", height: "1.35rem" }}
          title="DMI IN"
          aria-label="DMI IN"
        >
          <MdInput size={17} color="#0f766e" />
        </span>
        <span
          className={`d-inline-flex align-items-center justify-content-center ${item.dmi_out_all ? "" : "text-muted opacity-25"}`}
          style={{ width: "1.35rem", height: "1.35rem" }}
          title="DMI OUT"
          aria-label="DMI OUT"
        >
          <MdOutlineOutput size={17} color="#7c3aed" />
        </span>
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">{title}</h4>
      </div>
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
          <input
            type="text"
            className="form-control pe-5"
            placeholder="Search ERA requirements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
              }}
              aria-label="Clear search"
            >
              x
            </button>
          )}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="form-check form-switch m-0">
            <input
              id="era-req-hide-headings"
              type="checkbox"
              className="form-check-input"
              checked={hideHeadings}
              onChange={(e) => setHideHeadings(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="era-req-hide-headings">
              Hide headings
            </label>
          </div>
          <button
            type="button"
            className={`btn btn-sm ${showGrouped ? "btn-dark" : "btn-outline-dark"}`}
            onClick={() => setShowGrouped((current) => !current)}
          >
            {showGrouped ? "Show flat" : "Show grouped"}
          </button>
          {renderIconFilterButton(
            showDmiIn,
            () => setShowDmiIn((current) => !current),
            "DMI IN",
            <MdInput size={17} color="#0f766e" />
          )}
          {renderIconFilterButton(
            showDmiOut,
            () => setShowDmiOut((current) => !current),
            "DMI OUT",
            <MdOutlineOutput size={17} color="#7c3aed" />
          )}
          {renderIconFilterButton(
            showCoveredOnly,
            () => setShowCoveredOnly((current) => !current),
            "Covered",
            renderCoverageIcon()
          )}
          <span className="badge text-bg-secondary">
            Total: {totalVisibleItems}
          </span>
        </div>
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th
              style={{ ...stickyHeaderStyle, width: "160px", cursor: "pointer" }}
              onClick={() => handleSort("req_number")}
            >
              <span className="d-inline-flex align-items-center">
                Number
                {renderSortIndicator("req_number")}
              </span>
            </th>
            <th
              style={{ ...stickyHeaderStyle, cursor: "pointer" }}
              onClick={() => handleSort("req_title")}
            >
              <span className="d-inline-flex align-items-center">
                Title
                {renderSortIndicator("req_title")}
              </span>
            </th>
            <th
              className="text-center"
              style={{ ...stickyHeaderStyle, width: "90px", cursor: "pointer" }}
              onClick={() => handleSort("dmi_in_all")}
            >
              <span className="d-inline-flex align-items-center">
                DMI
                {renderSortIndicator("dmi_in_all")}
              </span>
            </th>
            <th style={{ ...stickyHeaderStyle, width: "120px" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {showGrouped ? groupedRequirements.map((group, groupIndex) => (
            <Fragment key={group.req_number}>
              {(() => {
                const groupRequirement = group.children[0];
                const singleRequirementGroup = group.children.length === 1 && isRequirement(groupRequirement);

                return (
              <tr style={group.node_type === "section" ? getSectionRowStyle(groupIndex) : getRowStyle(groupIndex)}>
                <td className={group.node_type === "section" ? "fw-bold text-primary-emphasis" : "fw-bold"}>
                  {group.children.length > 1 ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center me-2"
                      onClick={() => toggleGroup(group.req_number)}
                      aria-label={expandedGroups[group.req_number] ? "Collapse group" : "Expand group"}
                    >
                      {expandedGroups[group.req_number] ? (
                        <FaChevronDown />
                      ) : (
                        <FaChevronRight />
                      )}
                    </button>
                  ) : (
                    <span className="d-inline-block me-2" style={{ width: "14px" }} />
                  )}
                  {singleRequirementGroup ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 fw-bold text-decoration-none"
                      onClick={() => openRequirement(groupRequirement)}
                    >
                      {renderHighlightedText(group.req_number)}
                    </button>
                  ) : (
                    <span>{renderHighlightedText(group.req_number)}</span>
                  )}
                </td>
                <td className={group.node_type === "section" ? "fw-semibold text-primary-emphasis" : "text-muted"}>
                  {group.node_type === "section"
                    ? renderHighlightedText(group.children[0]?.req_title)
                    : singleRequirementGroup
                      ? renderHighlightedText(groupRequirement?.req_title)
                      : `${group.children.length} item${group.children.length === 1 ? "" : "s"}`}
                </td>
                <td className="text-center">
                  {renderDmiFlags({
                    node_type: group.node_type,
                    dmi_in_all: group.allDmiIn,
                    dmi_out_all: group.allDmiOut
                  })}
                </td>
                <td>
                  {canManage && singleRequirementGroup && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setEditingRequirement(groupRequirement);
                          setShowForm(true);
                        }}
                      >
                        <FaEdit />
                      </button>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteRequirement(groupRequirement.id)}
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                </td>
              </tr>
                );
              })()}
              {group.children.length > 1 && expandedGroups[group.req_number] && group.children.map((requirement, childIndex) => (
                <tr
                  key={requirement.id}
                  style={{
                    "--bs-table-bg": childIndex % 2 === 0
                      ? "rgba(25, 135, 84, 0.10)"
                      : "rgba(25, 135, 84, 0.03)"
                  }}
                >
                  <td className="ps-4 text-muted">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none text-muted"
                      onClick={() => openRequirement(requirement)}
                    >
                      {renderHighlightedText(requirement.req_number)}
                    </button>
                  </td>
                  <td>{renderHighlightedText(requirement.req_title)}</td>
                  <td className="text-center">
                    {renderDmiFlags(requirement)}
                  </td>
                  <td>
                    {canManage && isRequirement(requirement) && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => {
                            setEditingRequirement(requirement);
                            setShowForm(true);
                          }}
                        >
                          <FaEdit />
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteRequirement(requirement.id)}
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </Fragment>
          )) : sortedRequirements.map((requirement, index) => (
            <tr
              key={requirement.id}
              style={isSection(requirement) ? getSectionRowStyle(index) : getRowStyle(index)}
            >
              <td className={isSection(requirement) ? "fw-bold text-primary-emphasis" : "fw-bold"}>
                {isRequirement(requirement) ? (
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-bold text-decoration-none"
                    onClick={() => openRequirement(requirement)}
                  >
                    {renderHighlightedText(requirement.req_number)}
                  </button>
                ) : (
                  renderHighlightedText(requirement.req_number)
                )}
              </td>
              <td className={isSection(requirement) ? "fw-semibold text-primary-emphasis" : undefined}>
                {renderHighlightedText(requirement.req_title)}
              </td>
              <td className="text-center">
                {renderDmiFlags(requirement)}
              </td>
              <td>
                {canManage && isRequirement(requirement) && (
                  <>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingRequirement(requirement);
                        setShowForm(true);
                      }}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteRequirement(requirement.id)}
                    >
                      <FaTrash />
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <EraRequirementFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadRequirements}
        requirement={editingRequirement}
        apiBase={apiBase}
      />

      <EraRequirementModal
        show={showModal}
        onClose={() => setShowModal(false)}
        requirement={selectedRequirement}
        apiBase={apiBase}
        title={title}
      />
    </div>
  );
}


